// pusherUtils.js
'use strict';
import { pusher as pusherDebug } from '../resource/debug.js';
import {
  getPusherTargets,
  setPusherChannelTarget,
} from '../resource/pusherTargetStore.js';
import {
  COUNTER_ADDED,
  STREAM_ONLINE,
  STREAM_OFFLINE,
  STREAM_VIEWERS_UPDATED,
  GOAL_ADDED,
  GOAL_ENDED,
  GOAL_STARTED,
  GOAL_PROGRESS_UPDATED,
  DEVICE_ONLINE,
  DEVICE_OFFLINE,
} from '../serviceWorker/PusherEvents.js';

const debugLog = pusherDebug.extend('log');
const debugErrorLog = pusherDebug.extend('error');

/**
 * Build the legacy V1 user channel name.
 * @param {object} params
 * @param {string} params.userId - The user id.
 * @returns {string|undefined} `private-enc-user@${userId}` or undefined when userId is missing.
 * @example
 * getPrivateUserChannelName({ userId: 'abc' });
 * // => 'private-enc-user@abc'
 */
export const getPrivateUserChannelName = ({ userId }) =>
  userId ? `private-enc-user@${userId}` : undefined;

export const STREAM_RELATED_EVENTS = [
  COUNTER_ADDED,
  STREAM_ONLINE,
  STREAM_OFFLINE,
  STREAM_VIEWERS_UPDATED,
  GOAL_ADDED,
  GOAL_ENDED,
  GOAL_STARTED,
  GOAL_PROGRESS_UPDATED,
  DEVICE_ONLINE,
  DEVICE_OFFLINE,
];

const CRITICAL_CHANNEL_REGEXES = [
  /^private-enc-user-v2@.+/,
  /^private-enc-stream@([a-z\d]+)$/i,
  /^presence-enc-asset@([a-z\d]+)$/i,
  /^presence-enc-message@([a-z\d]+)$/i,
  /^private-enc-campaign@([a-z\d_]+)$/i,
];

/**
 * Determines if a given channel is considered critical.
 *
 * @param {object} options - The options object.
 * @param {string} options.channelName - The name of the channel to check.
 * @returns {boolean} True if the channel is critical, false otherwise.
 */
export const getIsCriticalChannel = ({ channelName }) =>
  !!CRITICAL_CHANNEL_REGEXES.find(regex => regex.test(channelName));

const USER_BOUND_CHANNEL_REGEXES = [/^presence-/, /^private-.+-v2@/];

/**
 * Determines if a given channel is bound to the logged-in user identity
 * and should be unsubscribed on logout.
 *
 * @param {object} options - The options object.
 * @param {string} options.channelName - The name of the channel to check.
 * @returns {boolean} True if the channel should be unsubscribed on logout.
 */
export const getIsUserBoundChannel = ({ channelName }) =>
  !!USER_BOUND_CHANNEL_REGEXES.find(regex => regex.test(channelName));

const getIsValidChannelName = ({ channelName }) => {
  if (channelName.includes('undefined')) {
    return false;
  }
  let result = true;
  switch (true) {
    case channelName.startsWith('private-enc-user@'): {
      const userId = channelName.replace('private-enc-user@', '');
      if (
        userId === undefined ||
        userId === 'undefined' ||
        userId.length !== 24
      ) {
        result = false;
      }
      break;
    }
    default:
      break;
  }
  return result;
};

/**
 * Update pusher channel target
 * @param {object} {target} - object with channel configs.
 * @param {string} {target.(channelName).subscriptionReference} - subscription reference for identify subscribe reason and source
 * @param {bool} {target.(channelName).shouldSubscribe} - toggle should subscribe status
 * @param {bool} {[target.(channelName).isCritical]} - is channel critical, default from getIsCriticalChannel().
 * @param {number} {[target.(channelName).expiryTimestamp]} - channels need to re-auth is created before this timestamp
 */
export const updatePusherChannelTarget = ({ target }) => {
  const log = debugLog.extend('updatePusherChannelTarget');
  const errorLog = debugErrorLog.extend('updatePusherChannelTarget');

  log('start', { target });

  const currentTarget = getPusherTargets();
  Object.keys(target).forEach(channelName => {
    if (!getIsValidChannelName({ channelName })) {
      errorLog('invalid channelName', { channelName });
      return;
    }
    const currentData = currentTarget[channelName] || {};
    const targetData = target[channelName];

    // cleanup extra no-subscribe references
    const subscriptionMap = Object.assign({}, currentData.subscriptionMap, {
      [targetData.subscriptionReference]: targetData.shouldSubscribe,
    });
    Object.keys(subscriptionMap)
      .filter(ref => !subscriptionMap[ref].shouldSubscribe)
      .slice(0, -8) // only keep 8 no-subscription refs
      .forEach(ref => {
        delete subscriptionMap[ref];
      });

    const subscriptionData = {
      channelName,
      isCritical:
        targetData.isCritical ??
        (currentData.isCritical || getIsCriticalChannel({ channelName })),
      subscriptionMap,
      timestamp: currentData.timestamp || Date.now(),
      expiryTimestamp: Math.max(
        targetData.expiryTimestamp || 0,
        // Date.now() to make sure new tabs get fresh channel data
        currentData.expiryTimestamp || Date.now()
      ),
    };

    setPusherChannelTarget({ channelName, subscriptionData });
    log('channel assigned', { channelName, subscriptionData });
  });
};
